import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mic, ChevronLeft, ChevronRight, Play, Music } from "lucide-react";

/**
 * FeaturedArtists — horizontal scrolling carousel showing approved artists,
 * ranked by total public-track plays. Renders nothing when there are zero
 * approved artists so the landing page stays clean until content exists.
 */
export default function FeaturedArtists() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/artists/featured");
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        // Non-fatal — landing page still renders without the carousel.
        console.warn("Featured artists load failed:", e?.message || e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scrollBy = (dx) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  // Section always renders — the concert hero + "Are you an artist?" CTA
  // stand alone even before any artists have been approved. The carousel
  // slot below fills in once there are approved artists.
  if (!loaded) return null;

  return (
    <section
      className="relative overflow-hidden bg-white border-b border-[#EAE3DE]"
      data-testid="featured-artists-section"
    >
      {/* CONCERT HERO — full-bleed image + tagline overlay. */}
      <div className="relative h-[380px] md:h-[460px] w-full overflow-hidden" data-testid="featured-artists-hero">
        <img
          src="https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920"
          alt="Live concert crowd with stage lights"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark scrim + red glow for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#B31E1E]/20 via-transparent to-transparent" />
        <div className="relative z-10 h-full max-w-6xl mx-auto px-6 md:px-10 flex items-end pb-10 md:pb-14">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 backdrop-blur border border-white/25 px-3 py-1 text-[11px] font-semibold tracking-wide text-white">
              <Mic size={11} /> Local music on Servrevu
            </span>
            <h2 className="mt-4 text-4xl md:text-6xl font-black tracking-tight text-white leading-[1.02] drop-shadow-2xl">
              Your city sounds
              <span className="block text-[#F0C9C4]">like this.</span>
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] text-white/85 max-w-xl leading-relaxed">
              Discover local bands and independent musicians. Stream their catalog, follow, and catch them live — all on Servrevu.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/playlists">
                <span className="inline-flex items-center gap-2 rounded-lg bg-white text-[#1B1614] hover:bg-[#F5EEE8] px-5 h-11 font-semibold text-[14px] shadow-lg transition-transform hover:scale-[1.03]" data-testid="artists-hero-explore">
                  Explore playlists <Play size={14} className="fill-current" />
                </span>
              </Link>
              <Link to="/playlists">
                <span className="inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/5 backdrop-blur text-white hover:bg-white/10 px-5 h-11 font-semibold text-[14px] transition-transform hover:scale-[1.03]" data-testid="artists-hero-become">
                  I&apos;m an artist <ChevronRight size={14} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Always render the "Trending bands" carousel section — falls back
          to a "Be the first" empty-state row when no artists have been
          approved yet. */}
      {items.length === 0 && (
        <div className="absolute inset-x-0 pointer-events-none bg-gradient-to-b from-[#FAF5F2] via-white to-white" style={{ top: "460px" }} />
      )}
      <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
              <Mic size={11} /> Trending this week
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tight text-[#1B1614] leading-[1.05]">
              Trending bands.
            </h2>
            <p className="mt-2 text-[15px] text-[#5C4E48] max-w-xl">
              Ranked by plays &amp; follows this week. Click through to hear their catalog and follow.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scrollBy(-320)}
              className="h-10 w-10 rounded-full border border-[#EAE3DE] bg-white hover:bg-[#FAF5F2] flex items-center justify-center transition-colors"
              aria-label="Scroll left"
              data-testid="featured-scroll-left"
            >
              <ChevronLeft size={16} className="text-[#1B1614]" />
            </button>
            <button
              onClick={() => scrollBy(320)}
              className="h-10 w-10 rounded-full border border-[#EAE3DE] bg-white hover:bg-[#FAF5F2] flex items-center justify-center transition-colors"
              aria-label="Scroll right"
              data-testid="featured-scroll-right"
            >
              <ChevronRight size={16} className="text-[#1B1614]" />
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          /* Empty-state placeholder row — invites the first artist to sign up.
              Uses static gradient tiles so the section never looks broken even
              before any approved band exists. */
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="trending-empty-state">
            {[
              { label: "Post rock", ord: "01" },
              { label: "Neo soul", ord: "02" },
              { label: "Indie folk", ord: "03" },
              { label: "Hip-hop", ord: "04" },
            ].map((g) => (
              <div
                key={g.ord}
                className="rounded-2xl border border-[#EAE3DE] overflow-hidden group"
                data-testid={`trending-placeholder-${g.ord}`}
              >
                <div className="relative aspect-square bg-gradient-to-br from-[#1B1614] via-[#8F1717] to-[#B31E1E] flex items-center justify-center">
                  <div className="text-white/30 text-[11px] font-mono tracking-widest">SPOT {g.ord}</div>
                </div>
                <div className="p-3 bg-white">
                  <div className="text-[13px] font-semibold text-[#1B1614]">Your band here</div>
                  <div className="text-[11px] text-[#8B7B73]">{g.label}</div>
                </div>
              </div>
            ))}
            <Link
              to="/playlists"
              className="col-span-2 sm:col-span-4 mt-2 rounded-xl border border-dashed border-[#B31E1E]/40 bg-[#F9E4E1]/40 px-5 py-4 flex items-center justify-between hover:bg-[#F9E4E1]/70 transition-colors"
              data-testid="trending-cta"
            >
              <span className="text-sm text-[#1B1614]">
                <strong>Be the first trending band on Servrevu.</strong> Upload a track, get playlisted, get discovered.
              </span>
              <span className="text-[#B31E1E] font-semibold text-sm inline-flex items-center gap-1">
                Start uploading <ChevronRight size={14} />
              </span>
            </Link>
          </div>
        ) : (
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          data-testid="featured-scroller"
        >
          {items.map((a, i) => (
            <Link
              key={a.id}
              to={`/artists/${a.id}`}
              className="group shrink-0 w-64 rounded-2xl border border-[#EAE3DE] bg-white overflow-hidden hover:border-[#B31E1E] hover:shadow-[0_10px_40px_-15px_rgba(179,30,30,0.28)] transition-all snap-start"
              data-testid={`featured-artist-${a.id}`}
            >
              {/* Cover — real photo if uploaded, else fall back to a red
                  gradient with the artist initials. */}
              <div className="relative aspect-square bg-gradient-to-br from-[#B31E1E] via-[#8F1717] to-[#3E2A26] flex items-center justify-center overflow-hidden">
                {a.cover_image_url ? (
                  <img
                    src={a.cover_image_url}
                    alt={a.stage_name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-5xl font-black tracking-tight text-white/95 drop-shadow-lg" style={{ letterSpacing: "-0.03em" }}>
                    {initialsFrom(a.stage_name)}
                  </span>
                )}
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all shadow-lg">
                    <Play size={16} className="text-[#B31E1E] ml-0.5 fill-current" />
                  </span>
                </div>
                {i === 0 && (
                  <span className="absolute top-3 left-3 rounded-full bg-white text-[#B31E1E] text-[10px] font-black tracking-wider uppercase px-2 py-0.5">
                    #1 this week
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="font-semibold text-[#1B1614] truncate">{a.stage_name}</div>
                <div className="text-xs text-[#8B7B73] mt-1 flex items-center gap-1.5 font-mono">
                  <Music size={11} className="text-[#B31E1E]" />
                  {a.track_count} track{a.track_count === 1 ? "" : "s"}
                  {a.total_plays > 0 && (
                    <span className="text-[#B31E1E]">· {formatCount(a.total_plays)} plays</span>
                  )}
                </div>
                {a.bio && (
                  <p className="mt-2 text-xs text-[#5C4E48] line-clamp-2 leading-relaxed">
                    {a.bio}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
        )}
      </div>
    </section>
  );
}

function initialsFrom(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatCount(n) {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
